/**
 * Turns a free/busy answer into the two lines Teams puts under the presence
 * label — and into NOTHING when the data is not there.
 *
 * `workingHours` is null for anyone who never configured a schedule, and the
 * blocks array is empty for anyone with a clear calendar. Both cases return
 * null here so the card omits the line: "Work hours: 09:00 AM - 05:00 PM"
 * rendered from a default would be a statement about a person that nobody in
 * this workspace ever made.
 */
import type { PersonAvailability } from '../api/peopleApi';
import { clockAt, formatTimeOfDay } from './localTime';

/** ISO weekday for an instant as seen in `timeZone`: 1 = Monday … 7 = Sunday. */
const ISO_WEEKDAY: Readonly<Record<string, number>> = {
  Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7,
};

function isoWeekdayIn(timeZone: string, at: Date): number {
  const short = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(at);
  return ISO_WEEKDAY[short] ?? 1;
}

/**
 * "08:00 AM - 05:00 PM" for the person's TODAY, or null.
 *
 * A day can hold several intervals (a split shift); the line spans the first
 * start to the last end, which is what "work hours" means to someone deciding
 * whether it is rude to call.
 */
export function workHoursLine(availability: PersonAvailability | null, at: Date = new Date()): string | null {
  const hours = availability?.workingHours;
  if (hours === undefined || hours === null) return null;
  const today = hours.schedule[String(isoWeekdayIn(hours.timezone, at))];
  if (today === undefined || today.length === 0) return null;
  const first = today[0];
  const last = today[today.length - 1];
  if (first === undefined || last === undefined) return null;
  const start = formatTimeOfDay(first.start);
  const end = formatTimeOfDay(last.end);
  if (start === null || end === null) return null;
  return `${start} - ${end}`;
}

/** A busy block that has not finished yet, sorted by start. */
function upcomingBlocks(availability: PersonAvailability, at: Date): PersonAvailability['blocks'] {
  return availability.blocks
    .filter((block) => block.status !== 'free' && new Date(block.endsAt).getTime() > at.getTime())
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
}

/**
 * "Free until 3:00 PM" / "Busy until 3:00 PM", in the VIEWER's zone — the
 * reader is deciding whether to interrupt now, so the clock has to be theirs.
 * Null when the calendar says nothing about the next day.
 */
export function calendarLine(
  availability: PersonAvailability | null,
  viewerZone: string,
  at: Date = new Date(),
): string | null {
  if (availability === null) return null;
  const blocks = upcomingBlocks(availability, at);
  const next = blocks[0];
  if (next === undefined) return null;

  const busyNow = new Date(next.startsAt).getTime() <= at.getTime();
  if (!busyNow) return `Free until ${clockAt(next.startsAt, viewerZone)}`;

  // Back-to-back meetings read as one stretch — walk the chain so the card says
  // when they are actually free, not when this particular block ends.
  let freeAt = new Date(next.endsAt).getTime();
  for (const block of blocks.slice(1)) {
    if (new Date(block.startsAt).getTime() > freeAt) break;
    freeAt = Math.max(freeAt, new Date(block.endsAt).getTime());
  }
  return `Busy until ${clockAt(new Date(freeAt).toISOString(), viewerZone)}`;
}
